/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { DI, DIProps, parseJwt } from './Core';
import { hideNotification, loginStatus } from './Actions';
import { Card, FooterHelp, InlineStack, Icon, Link, Page, Toast } from '@shopify/polaris';
import Auth from './Components/Auth';
import Panel, { RenderHeadersAction } from './Components/Panel/Panel';
import ShowMessage from './Components/Other/Message/ShowMessage';
import Support from './Components/Panel/Support/components/Support';
import { getClientExpTime, isAdminLoggedIn, openAppInEmbedded, RedirectToHome, RenderSkeleton } from './Components/Panel/Panel/index';
import MaintenancePage from './Components/Other/Maintenance/MaintenancePage';
import { CircleInformationMajor } from '@shopify/polaris-icons';
import './styles.css'
import NewFaq from './Components/Panel/Faq/components/NewFaq';
import TourComponent from './Components/Panel/TourComponent';
import Unsubscribe from './Components/Panel/Config/Component/Notifications/Unsubscribe';
import KnowledgeBase from './Components/Panel/KnowledgeBase/KnowledgeBase';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

interface PropsI extends DIProps {
    hideNotification: (id: number | string) => void;
    loginStatus: () => void;
    syncNecessaryInfo: () => void;
    syncConnectorInfo: (props: any, shop_url?: string | null, updateCurrentShop?: boolean, updateStepStatus?: React.Dispatch<React.SetStateAction<string>>) => void;
    syncProfileInfo: () => void;
    syncServices: () => void;
}

function checkForAuto(Props: PropsI) {
    const { location } = Props;
    let params: any = new URLSearchParams(location.search);
    if (Props.di.globalState.get('auth_token') && Props.di.globalState.get('shop') === params.get('shop')) {
        const tokenDecoded = parseJwt(Props.di.globalState.get('auth_token') ?? '');
        if (getClientExpTime(tokenDecoded['exp'], 1)) {
            return 'showApp';
        }
    }
    return 'loading'
}

function App(Props: PropsI): JSX.Element {
    const { t } = useTranslation();
    const [maintenanceData, setMaintenanceData] = useState<any>({});
    const [loadingType, setLoadingType] = useState(checkForAuto(Props)); // loading
    const navigate = useNavigate();

    useEffect(() => {

        const URLBYPASS = [
            '/show/message',
            '/panel/support',
            '/panel/faq',
            '/panel/unsubscribe',
            '/panel/knowledgeBase'
        ];
        if (loadingType === "showMaintenance" && Props.di.globalState.get("setMaintenanceFlagOff") === "openApp") {
            genarateToken();
        } else if (process.env.REACT_APP_PROD === "0" || isAdminLoggedIn() || URLBYPASS.indexOf(Props.location.pathname) != -1) {
            setLoadingType("local")
        } else if (loadingType != "showMaintenance" && loadingType != 'showApp') {
            genarateToken();
        }

    }, []);

    const genarateToken = () => {
        const { location } = Props;
        let params: any = new URLSearchParams(location.search);
        const entries = params.entries();
        params = paramsToObject(entries);

        if (typeof params['admin_user_token'] === "string") {
            setLoadingType("local");
            return true;
        }
        if (typeof params['user_token'] === "string") {
            setLoadingType('openApp');
            return true;
        }

        Props.di.globalState.removeLocalStorage('auth_token');
        Props.di.globalState.removeLocalStorage('is_plan_active');
        Props.di.globalState.removeLocalStorage('onboarding_completed');
        Props.di.GET('shopifyhome/migration/isUserExistsInMulti', params)
            .then((multiAccountUserCheck: any) => {
                if ((params.shop !== null) && multiAccountUserCheck['success']) {
                    if (multiAccountUserCheck['showMaintenance']) {
                        setMaintenanceData({ ...multiAccountUserCheck })
                        setLoadingType('showMaintenance');
                        return true;
                    }
                    let user_details = {
                        user_id: multiAccountUserCheck['data']['user_id'],
                        host: params.host ?? Props.di.globalState.get("host"),
                        shop: params.shop ?? Props.di.globalState.get("shop"),
                        source_shop_id: multiAccountUserCheck['data']['source_shop_id'],
                        target_shop_id: multiAccountUserCheck['data']['target_shop_id'],
                        username: multiAccountUserCheck['data']['username'],
                        token: multiAccountUserCheck['data']['token'],
                        plan: multiAccountUserCheck['data']['plan'],
                        on_boarding_status: multiAccountUserCheck['data']['on_boarding_status'],
                    };
                    Props.di.globalState.set('topBanner', JSON.stringify(multiAccountUserCheck?.['data']?.['topBanner'] ?? {}));
                    Props.di.globalState.set('page_visited', JSON.stringify(multiAccountUserCheck['data']['page_visited'] ?? ''));


                    if (user_details?.plan['success']) {
                        Props.di.globalState.set(`is_plan_active`, 'true')
                    }
                    if (user_details?.on_boarding_status) {
                        Props.di.globalState.set('onboarding_completed', '_COMPLETED_');
                    }
                    Props.di.globalState.set('auth_token', user_details.token);
                    Props.di.globalState.set('user_id', user_details.user_id);
                    Props.di.globalState.set('host', user_details.host);
                    Props.di.globalState.set('shop', user_details.shop);
                    Props.di.globalState.set('source_id', user_details.source_shop_id);
                    if (Props.di.globalState.get('target_id') == null) {
                        // let external_target_id = Props.di.globalState.get('external_target_id');
                        if (params?.target_shop_id) {
                            Props.di.globalState.set('target_id', params?.target_shop_id);
                            // Props.di.globalState.removeLocalStorage('external_target_id');
                        }
                        else {
                            Props.di.globalState.set('target_id', user_details.target_shop_id);
                        }
                    }
                    Props.di.globalState.set('target', 'amazon');
                    Props.di.globalState.set('source', 'shopify');

                    Props.di.globalState.set('all_shops', JSON.stringify({
                        [user_details.shop]: user_details
                    }));
                    setLoadingType('showApp');
                    return true;
                } else {
                    setLoadingType('installApp');
                }
            });

    };

    function paramsToObject(entries: any) {
        const result: any = {}
        for (const [key, value] of entries) {
            result[key] = value;
        }
        return result;
    }

    if (loadingType === "loading") {
        return <RenderSkeleton {...Props} />
    } else if (loadingType === "openApp") {
        openAppInEmbedded(Props);
        return <RenderSkeleton {...Props} />
    } else if (loadingType === "installApp") {
        const panelProps: any = Props
        RedirectToHome(panelProps);
        return <RenderSkeleton {...Props} />
    } else if (loadingType === "showMaintenance") {
        return <MaintenancePage maintenanceData={maintenanceData} />;
    }

    return (
        <TourComponent>

            <Routes>
                <Route
                    path="/auth"
                    element={
                        <Suspense fallback={<></>}>
                            <Auth />
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>
                <Route
                    path="/panel/:uId/*"
                    element={
                        <Suspense fallback={<></>}>
                            <Panel isUserExistsInMultiData={{ ...maintenanceData, key: "value" }} />
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>
                <Route
                    path="/panel/support"
                    element={
                        <Suspense fallback={<></>}>
                            <Support />
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>
                <Route
                    path="/panel/knowledgeBase"
                    element={
                        <Suspense fallback={<></>}>
                            <KnowledgeBase />
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>

                <Route
                    path="/panel/knowledgeBase/:s_id/:kb_id"
                    element={
                        <Suspense fallback={<></>}>
                            <KnowledgeBase />
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>

                <Route
                    path="/panel/knowledgeBase/:s_id/:sb_id/:kb_id"
                    element={
                        <Suspense fallback={<></>}>
                            <KnowledgeBase />
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>

                <Route
                    path="/panel/faq"
                    element={
                        <Suspense fallback={<></>}>
                            <Page
                                fullWidth={false}
                                title={t("FAQ & Troubleshoot")}
                                subtitle={t("Find solutions to all your Queries.")}
                                primaryAction={
                                    <RenderHeadersAction {...Props} show_only_lng_switcher />
                                }
                            >
                                {/* <Faq /> */}
                                <NewFaq />
                                <FooterHelp>
                                    <InlineStack gap="100">
                                        <div>
                                            <Icon
                                                source={CircleInformationMajor}
                                                tone="info"
                                            />
                                        </div>{t("Learn more about")}<Link target='_blank' url="https://support.cedcommerce.com/portal/en/kb/articles/article-28-3-2025">
                                            {t("CedCommerce Amazon Channel")}
                                        </Link>,<Link target='_blank' url='https://amazon-sales-channel-app-backend.cifapps.com/Cedcommerce_Privacy_Statement.pdf'>
                                            {t("Terms & Conditions")}
                                        </Link>{t("and")}<Link target='_blank' url='https://amazon-sales-channel-app-backend.cifapps.com/Data_Privacy_Rights.pdf'>
                                            {t("Data Privacy Rights")}
                                        </Link>. {t("Need Help?")}<Link onClick={() => navigate('../../panel/support')}>
                                            {t("Get Support")}
                                        </Link>{t("or")}<Link target='_blank' url="https://calendly.com/cedcommerce-awe-sales/sales" >
                                            {t("Book a Call")}
                                        </Link>
                                    </InlineStack>
                                </FooterHelp>
                            </Page>
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>
                <Route
                    path="/panel/unsubscribe"
                    element={
                        <Suspense fallback={<></>}>
                            <Unsubscribe />
                            <FooterHelp>
                                <InlineStack gap="100">
                                    <div>
                                        <Icon
                                            source={CircleInformationMajor}
                                            tone="info"
                                        />
                                    </div>{t("Learn more about")}<Link target='_blank' url="https://support.cedcommerce.com/portal/en/kb/articles/article-28-3-2025">
                                        {t("CedCommerce Amazon Channel")}
                                    </Link>,<Link target='_blank' url='https://amazon-sales-channel-app-backend.cifapps.com/Cedcommerce_Privacy_Statement.pdf'>
                                        {t("Terms & Conditions")}
                                    </Link>{t("and")}<Link target='_blank' url='https://amazon-sales-channel-app-backend.cifapps.com/Data_Privacy_Rights.pdf'>
                                        {t("Data Privacy Rights")}
                                    </Link>. {t("Need Help?")}<Link onClick={() => navigate('../../panel/support')}>
                                        {t("Get Support")}
                                    </Link>{t("or")}<Link target='_blank' url="https://calendly.com/cedcommerce-awe-sales/sales" >
                                        {t("Book a Call")}
                                    </Link>
                                </InlineStack>
                            </FooterHelp>
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>
                <Route
                    path="/show/message"
                    element={
                        <Suspense fallback={<></>}>
                            <ShowMessage />
                        </Suspense>
                    }>
                    <Route path="*" element={<Card padding='0' roundedAbove='sm'>NO Page Found 2</Card>} />
                </Route>
                <Route path="*" element={<Navigate to={process.env.REACT_APP_PROD === "0" ? '/auth/login' : '/panel/user/home'} />} />
            </Routes>
            <RenderToasts {...Props} />
        </TourComponent>
    );
}

function RenderToasts(props: PropsI): JSX.Element {
    const { redux } = props;
    const { showToast } = redux;
    return (
        <React.Fragment>
            {Object.keys(showToast).map((key: any) => {
                const toast = showToast[key];
                let type: any = 'success';
                if (toast.error) type = 'error';
                if (toast.warn) type = 'warning';
                if (toast.info) type = 'info';
                if (toast.action || toast.action !== undefined) {
                    return (
                        <div className={type == 'info' ? 'custom__toast--info' : ''} key={key}>
                            <Toast
                                key={key}
                                error={toast.error}
                                onDismiss={() => props.hideNotification(key)}
                                content={toast.message}
                                action={{
                                    content: toast.action?.content,
                                    onAction: toast.action?.onAction
                                }}
                                duration={toast.duration ?? 6000}
                            />
                        </div>
                    )
                }
                else {
                    return (
                        <div className={type == 'info' ? 'custom__toast--info' : ''} key={key}>
                            <Toast
                                key={key}
                                error={toast.error}
                                onDismiss={() => props.hideNotification(key)}
                                content={toast.message}
                                duration={toast.duration ?? 6000}
                            />
                        </div>
                    );
                }
            })}
        </React.Fragment>
    );
}

export const aAppVlue: { [name: string]: any } = {};

export const aAppdef = (key: string, append: any) => {
    aAppVlue[key] = append;
}

export default DI(App, { stateNeeded: true, func: { hideNotification, loginStatus } });
